import { useMemo, useState } from "react";
import { toast } from "@/lib/toast";
import { Compass } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useSeedDiscoveryStore, type SeedCandidate } from "@/stores/seed-discovery-store";
import { useAddPapers, usePapers } from "@/hooks/usePapers";
import { useSeedDiscovery } from "@/hooks/useSeedDiscovery";
import { candidateToPaperCreate } from "@/lib/seedPayload";
import { sourceLabel, truncateAuthors } from "@/lib/discovery-labels";

interface Props {
  conceptId: string;
}

export function SeedDiscoveryDialog({ conceptId }: Props) {
  const candidates = useSeedDiscoveryStore((s) => s.candidates);
  const reset = useSeedDiscoveryStore((s) => s.reset);
  const addPapers = useAddPapers(conceptId);
  const { discover } = useSeedDiscovery(conceptId);

  // Cached candidates can outlive an "add seeds" round-trip, so a re-open may
  // list a paper already on the concept. Filter against the existing seeds
  // (same role + same query the parent route already loaded → no extra fetch).
  const { data: foundationalData } = usePapers(conceptId, {
    role: "foundational",
    mode: "all",
  });
  const existingSeedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of foundationalData?.papers ?? []) {
      if (p.paper_id) ids.add(p.paper_id);
    }
    return ids;
  }, [foundationalData]);

  const visibleCandidates = useMemo(
    () => (candidates ?? []).filter((c) => !existingSeedIds.has(c.paper_id)),
    [candidates, existingSeedIds],
  );

  // No candidate is cited by any other: either the selector fell back to
  // citation order, or the set is not a coherent literature. Either way the
  // user should read each one before accepting — the 2026-09-21 beta report
  // was an all-zero set that shipped ten off-topic seeds without a hint.
  const lowConfidence =
    visibleCandidates.length > 0 &&
    visibleCandidates.every((c) => (c.overlap_score ?? 0) === 0);
  const usedFallback = visibleCandidates.some((c) => c.selection === "fallback");

  // Derived, not a persisted run status: may under-claim (OpenAlex ran but
  // found nothing in the top 10), never over-claims.
  const searchedOpenAlex = visibleCandidates.some((c) => c.sources?.includes("openalex"));
  // The backend attaches `sources` only when OpenAlex actually contributed to
  // the run; while the feature is dark (or for saved legacy candidates) no
  // candidate has it, and the UI stays exactly as before.
  const showSources = visibleCandidates.some((c) => Array.isArray(c.sources));

  const [selected, setSelected] = useState<Set<string>>(() =>
    new Set(visibleCandidates.map((c) => c.paper_id)),
  );

  // Re-initialize selections whenever the visible set changes (re-discover, or
  // existing-seed data loading in after first render).
  const [prevVisible, setPrevVisible] = useState(visibleCandidates);
  if (visibleCandidates !== prevVisible) {
    setPrevVisible(visibleCandidates);
    setSelected(new Set(visibleCandidates.map((c) => c.paper_id)));
  }

  if (!candidates || candidates.length === 0) return null;

  // Candidates exist but every one is already on the concept (cached set
  // re-opened after the papers were added). Show a resolved state instead of
  // a misleading empty list or silently rendering nothing.
  const allAlreadyAdded = visibleCandidates.length === 0;

  const toggleOne = (paperId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(paperId)) next.delete(paperId);
      else next.add(paperId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === visibleCandidates.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleCandidates.map((c) => c.paper_id)));
    }
  };

  const handleConfirm = () => {
    const chosen = visibleCandidates.filter((c) => selected.has(c.paper_id));
    if (chosen.length === 0) {
      reset();
      return;
    }

    addPapers.mutate(
      {
        papers: chosen.map(candidateToPaperCreate),
        role: "foundational",
        added_source: "seed_discovery",
      },
      {
        onSuccess: () => {
          toast.success(`Added ${chosen.length} seed paper${chosen.length === 1 ? "" : "s"}`);
          reset();
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={() => reset()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Seed Papers Found</DialogTitle>
          {showSources && (
            <p data-testid="seed-searched" className="text-xs text-muted-foreground">
              {searchedOpenAlex
                ? "Searched Semantic Scholar and OpenAlex."
                : "Searched Semantic Scholar."}
            </p>
          )}
        </DialogHeader>

        {allAlreadyAdded ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10 text-center">
            <Compass className="size-8 text-muted-foreground/40" />
            <div className="space-y-1">
              <p className="text-sm font-medium">All these seeds are already on your concept</p>
              <p className="text-xs text-muted-foreground">
                Re-discover to find a fresh set of candidates.
              </p>
            </div>
          </div>
        ) : (
        <div className="flex-1 overflow-y-auto space-y-1 py-2">
          {lowConfidence && (
            <div
              role="status"
              data-testid="seed-low-confidence"
              className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200/90"
            >
              <span className="font-semibold">Check these before accepting.</span>{" "}
              {usedFallback
                ? "The seed selector didn't respond, so these are the most-cited papers matching your concept's terms."
                : "None of these papers cite each other, so they may not be a coherent starting set for this concept."}{" "}
              Untick anything that isn't about your topic.
            </div>
          )}
          <button
            onClick={toggleAll}
            className="mb-2 text-xs text-muted-foreground hover:text-foreground transition-colors px-1"
          >
            {selected.size === visibleCandidates.length ? "Deselect all" : "Select all"}
          </button>

          {visibleCandidates.map((c: SeedCandidate) => (
            <label
              key={c.paper_id}
              className="flex items-start gap-3 rounded-md px-3 py-2 hover:bg-muted/50 cursor-pointer transition-colors"
            >
              <Checkbox
                checked={selected.has(c.paper_id)}
                onCheckedChange={() => toggleOne(c.paper_id)}
                className="mt-1"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium leading-snug">
                    {c.title}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {truncateAuthors(c.authors)}
                  {c.year ? `, ${c.year}` : ""}
                  {c.journal ? ` · ${c.journal}` : ""}
                  {showSources && (
                    <>
                      {" · "}
                      <span data-testid={`seed-source-${c.paper_id}`}>{sourceLabel(c.sources)}</span>
                    </>
                  )}
                </div>
                {c.reason && (
                  <div className="mt-1">
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground/70">
                      Why it's a seed
                    </div>
                    <div className="text-xs text-muted-foreground/90 leading-snug">
                      {c.reason}
                    </div>
                  </div>
                )}
                <div className="flex gap-1.5 mt-1">
                  {c.citation_count > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      {c.citation_count.toLocaleString()} citations
                    </Badge>
                  )}
                  {c.overlap_score >= 1 && (
                    <Badge className="text-[10px] px-1.5 py-0 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      Cited by {c.overlap_score} other{c.overlap_score === 1 ? "" : "s"}
                    </Badge>
                  )}
                </div>
              </div>
            </label>
          ))}
        </div>
        )}

        <DialogFooter>
          <WhyDisabled reason={addPapers.isPending ? "Adding seeds…" : null} className="mr-auto">
            <Button
              variant="outline"
              onClick={() => discover(true)}
              disabled={addPapers.isPending}
              className="gap-1.5"
              title="Find a different set of candidates (re-runs discovery)"
            >
              <Compass className="size-3.5" />
              Re-discover
            </Button>
          </WhyDisabled>
          <Button variant="ghost" onClick={() => reset()}>
            {allAlreadyAdded ? "Close" : "Cancel"}
          </Button>
          {!allAlreadyAdded && (
            <Button
              onClick={handleConfirm}
              disabled={addPapers.isPending}
              className="min-w-[9rem] whitespace-nowrap"
            >
              {addPapers.isPending
                ? "Adding..."
                : `Add ${selected.size} as seed${selected.size === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
